// src/Components/OrderManagement.tsx

import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../Redux/Store';
import {
  acceptOrder,
  rejectOrder,
  cancelOrder,
  restoreOrder,
} from '../../Redux/ActiveStore/orderManageApi';
import { fetchOrdersAsync, resetOrderState } from '../../Redux/ActiveStore/orderManageSlice';
import SortIcon from '@mui/icons-material/Sort';
import TuneIcon from '@mui/icons-material/Tune';
import { Menu, MenuItem, Button, Box, Card, CardContent, Chip } from '@mui/material';
import OrderConfirmationModal from './OrderConfirmationModal'; // Adjust the path as necessary
import ConfirmDialog from '../../Components/Dialog/ConfirmDialog';

interface OrderManagementProps {
  activeStoreId: string | undefined;
  orderNotificationData: { 
    storeId: string, 
    activeStoreId: string, 
    activeDiningTableId: string, 
    notificationType: string,
    activeOrderId: string // 必須として追加
  }[];
}

const OrderManagement: React.FC<OrderManagementProps> = ({ activeStoreId, orderNotificationData }) => {
  const dispatch: AppDispatch = useDispatch();
  const { orders, carts, status } = useSelector((state: RootState) => state.orderManage);
  const [selectedCart, setSelectedCart] = useState<string>('');
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [targetCartId, setTargetCartId] = useState<string>('');
  const [targetOrderId, setTargetOrderId] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('WAITING');
  const [diningTableFilter, setDiningTableFilter] = useState<string>('ALL');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [statusFilterAnchorEl, setStatusFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [diningTableFilterAnchorEl, setDiningTableFilterAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (activeStoreId) {
      dispatch(fetchOrdersAsync(activeStoreId));
    }
  }, [dispatch, activeStoreId]);

  // 注文情報を更新する関数
  const fetchOrderData = async () => {
    if (!activeStoreId) return;
    
    try {
      console.log('Resetting order state before fetching new data');
      dispatch(resetOrderState());
      
      console.log('Fetching orders for activeStoreId:', activeStoreId);
      const result = await dispatch(fetchOrdersAsync(activeStoreId)).unwrap();
      console.log('Orders updated successfully, received data:', result);
      
      // フィルターのリセットは削除（無限ループの原因になる可能性があるため）
    } catch (error) {
      console.error('Failed to update orders:', error);
    }
  };

  // 定期的なリロードのための参照
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // 1分ごとに自動リロード
  useEffect(() => {
    if (activeStoreId) {
      // 初回読み込み
      fetchOrderData();
      
      // 1分ごとに自動リロード
      intervalRef.current = setInterval(() => {
        console.log('Auto-reloading orders (1-minute interval)');
        fetchOrderData();
      }, 60000); // 60秒 = 1分
    }
    
    // クリーンアップ関数
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [activeStoreId]); // activeStoreIdが変わったときだけ再設定

  // 最後に処理した通知のインデックスを追跡
  const lastProcessedIndexRef = useRef<number>(-1);
  
  // 処理済み通知のActiveOrderIdを追跡するための状態
  const [processedActiveOrderIds, setProcessedActiveOrderIds] = useState<Set<string>>(new Set());
  
  // 注文通知を監視して状態を更新（通知ごとに処理）
  useEffect(() => {
    console.log('OrderNotificationData changed:', orderNotificationData);
    
    // 通知データが存在する場合に処理を実行
    if (orderNotificationData.length > 0 && activeStoreId) {
      // 前回処理した以降の新しい通知のみを処理
      if (orderNotificationData.length > lastProcessedIndexRef.current + 1) {
        // 新しい通知のみをフィルタリング
        const newNotifications = orderNotificationData.slice(lastProcessedIndexRef.current + 1);
        
        // cartCheckout通知をフィルタリング
        const cartCheckoutNotifications = newNotifications.filter(
          notification => notification.notificationType === 'cartCheckout' && 
                          notification.activeStoreId === activeStoreId
        );
        
        if (cartCheckoutNotifications.length > 0) {
          console.log('Processing new cart checkout notifications:', cartCheckoutNotifications);
          
          // 新しいActiveOrderIdを持つ通知のみを処理
          const newActiveOrderIds = cartCheckoutNotifications
            .filter(notification => {
              // activeOrderIdがない場合はactiveStoreIdを使用
              const activeOrderId = notification.activeOrderId || notification.activeStoreId;
              return !processedActiveOrderIds.has(activeOrderId);
            })
            .map(notification => notification.activeOrderId || notification.activeStoreId);
          
          if (newActiveOrderIds.length > 0) {
            console.log('Found new ActiveOrderIds:', newActiveOrderIds);
            
            // 通知を受け取ったら即座に注文情報を更新（1回だけ）
            fetchOrderData();
            
            // 処理済みActiveOrderIdのセットを更新
            setProcessedActiveOrderIds(prev => {
              const newSet = new Set(prev);
              newActiveOrderIds.forEach(id => {
                if (id) newSet.add(id);
              });
              return newSet;
            });
          } else {
            console.log('No new ActiveOrderIds found, skipping fetch');
          }
          
          // 最後に処理した通知のインデックスを更新
          lastProcessedIndexRef.current = orderNotificationData.length - 1;
        }
      }
    }
  }, [orderNotificationData.length, activeStoreId, processedActiveOrderIds]); // orderNotificationData.lengthとprocessedActiveOrderIdsを依存配列に含める

  useEffect(() => {
    // 必要に応じてソート順を調整
    if (statusFilter === 'WAITING') {
      setSortOrder('asc');
    } else {
      setSortOrder('desc');
    }
  }, [statusFilter]);

  useEffect(() => {
    const handleBodyStyle = (event: any) => {
      if (event.target.closest('.MuiPopover-root')) {
        document.body.style.padding = '0px';
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.padding = '0px';
        document.body.style.overflow = '';
      }
    };

    document.addEventListener('click', handleBodyStyle);

    return () => {
      document.removeEventListener('click', handleBodyStyle);
      document.body.style.padding = '0px';
      document.body.style.overflow = '';
    };
  }, []);

  const handleCartClick = (cartUniqueId: string) => {
    setSelectedCart(cartUniqueId);
  };

  const handleCloseClick = () => {
    setSelectedCart('');
  };

  const handleAcceptOrder = async (cartUniqueId: string) => {
    try {
      setTargetCartId(cartUniqueId);
      setAcceptDialogOpen(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleConfirmAccept = async () => {
    if (!activeStoreId || !targetCartId) return;
    
    try {
      await acceptOrder(activeStoreId, targetCartId);
      dispatch(fetchOrdersAsync(activeStoreId));
      setSelectedCart('');
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setAcceptDialogOpen(false);
    }
  };

  const handleRejectOrder = async (cartUniqueId: string) => {
    try {
      setTargetCartId(cartUniqueId);
      setRejectDialogOpen(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleConfirmReject = async () => {
    if (!activeStoreId || !targetCartId) return;
    
    try {
      await rejectOrder(activeStoreId, targetCartId);
      dispatch(fetchOrdersAsync(activeStoreId));
      setSelectedCart('');
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setRejectDialogOpen(false);
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    try {
      setTargetOrderId(orderId);
      setCancelDialogOpen(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleConfirmCancel = async () => {
    if (!activeStoreId || !targetOrderId) return;
    
    try {
      await cancelOrder(activeStoreId, targetOrderId);
      dispatch(fetchOrdersAsync(activeStoreId));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setCancelDialogOpen(false);
    }
  };

  const handleRestoreOrder = async (orderId: string) => {
    try {
      setTargetOrderId(orderId);
      setRestoreDialogOpen(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleConfirmRestore = async () => {
    if (!activeStoreId || !targetOrderId) return;
    
    try {
      await restoreOrder(activeStoreId, targetOrderId);
      dispatch(fetchOrdersAsync(activeStoreId));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setRestoreDialogOpen(false);
    }
  };

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleStatusFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setStatusFilterAnchorEl(event.currentTarget);
  };

  const handleDiningTableFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setDiningTableFilterAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleStatusFilterClose = () => {
    setStatusFilterAnchorEl(null);
  };

  const handleDiningTableFilterClose = () => {
    setDiningTableFilterAnchorEl(null);
  };

  const handleSortChange = (sortOption: 'asc' | 'desc') => {
    setSortOrder(sortOption);
    handleSortClose();
  };

  const handleStatusFilterChange = (filterOption: string) => {
    setStatusFilter(filterOption);
    handleStatusFilterClose();
  };

  const handleDiningTableFilterChange = (filterOption: string) => {
    setDiningTableFilter(filterOption);
    handleDiningTableFilterClose();
  };

  const getUniqueDiningTableNames = () => {
    const names = carts.map((cart: any) => cart.diningTableName);
    return Array.from(new Set(names));
  };

  const filteredCarts = carts
    .filter((cart: any) => {
      // ステータスフィルタの適用
      if (statusFilter !== 'ALL' && cart.status !== statusFilter) {
        return false;
      }
      // ダイニングテーブルフィルタの適用
      if (diningTableFilter !== 'ALL' && cart.diningTableName !== diningTableFilter) {
        return false;
      }
      return true;
    });

  const sortedCarts = [...filteredCarts].sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'WAITING':
        return { label: '未処理', style: { backgroundColor: '#FF2497' } };
      case 'ACCEPTED':
        return { label: '受付済み', style: { backgroundColor: '#4CAF50' } };
      case 'REJECTED':
        return { label: 'キャンセル', style: { backgroundColor: '#777' } };
      case 'CLERK_ORDER':
        return { label: '店員注文', style: { backgroundColor: '#4CAF50' } };
      case 'COMPLETED':
        return { label: '会計済み', style: { backgroundColor: '#777' } };
      default:
        return { label: '', style: {} };
    }
  };

  return (
    <Box sx={{ p: 2, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {/* Sort Button */}
        <div>
          <Button
            startIcon={<SortIcon />}
            onClick={handleSortClick}
            variant="outlined"
            size="small"
          >
            並び替え
          </Button>
          <Menu
            anchorEl={sortAnchorEl}
            open={Boolean(sortAnchorEl)}
            onClose={handleSortClose}
          >
            <MenuItem onClick={() => handleSortChange('asc')}>昇順</MenuItem>
            <MenuItem onClick={() => handleSortChange('desc')}>降順</MenuItem>
          </Menu>
        </div>

        {/* Status Filter Button */}
        <div>
          <Button
            startIcon={<TuneIcon />}
            onClick={handleStatusFilterClick}
            variant="outlined"
            size="small"
          >
            ステータス
          </Button>
          <Menu
            anchorEl={statusFilterAnchorEl}
            open={Boolean(statusFilterAnchorEl)}
            onClose={handleStatusFilterClose}
          >
            <MenuItem onClick={() => handleStatusFilterChange('ALL')}>全て</MenuItem>
            <MenuItem onClick={() => handleStatusFilterChange('WAITING')}>未処理</MenuItem>
            <MenuItem onClick={() => handleStatusFilterChange('ACCEPTED')}>受付済み</MenuItem>
            <MenuItem onClick={() => handleStatusFilterChange('REJECTED')}>キャンセル</MenuItem>
            <MenuItem onClick={() => handleStatusFilterChange('CLERK_ORDER')}>店員注文</MenuItem>
            <MenuItem onClick={() => handleStatusFilterChange('COMPLETED')}>会計済み</MenuItem>
          </Menu>
        </div>

        {/* Dining Table Filter Button */}
        <div>
          <Button
            startIcon={<TuneIcon />}
            onClick={handleDiningTableFilterClick}
            variant="outlined"
            size="small"
          >
            テーブル
          </Button>
          <Menu
            anchorEl={diningTableFilterAnchorEl}
            open={Boolean(diningTableFilterAnchorEl)}
            onClose={handleDiningTableFilterClose}
          >
            <MenuItem onClick={() => handleDiningTableFilterChange('ALL')}>全て</MenuItem>
            {getUniqueDiningTableNames().map((tableName) => (
              <MenuItem key={tableName} onClick={() => handleDiningTableFilterChange(tableName)}>
                {tableName}
              </MenuItem>
            ))}
          </Menu>
        </div>
      </Box>

      {status === 'loading' && <p>Loading...</p>}
      {status === 'succeeded' && sortedCarts.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2, overflow: 'visible' }}>
          {sortedCarts.map((cart: any) => {
            const { label, style } = getStatusLabel(cart.status);
            return (
              <Card key={cart.cartUniqueId} sx={{ position: 'relative', overflow: 'visible' }}>
                <Chip
                  label={label}
                  sx={{
                    position: 'absolute',
                    top: -14,
                    left: -14,
                    fontSize: '12px',
                    fontWeight: 'bold',
                    ...style,
                  }}
                />
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ fontWeight: 'bold' }}>{cart.diningTableName}</Box>
                    <Box>{cart.numberOfVisitors}名様</Box>
                    <Box>{new Date(cart.createdAt).toLocaleTimeString()}</Box>
                    <Button variant="contained" color="warning" onClick={() => handleCartClick(cart.cartUniqueId)}>
                      受注内容
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Order Confirmation Modal */}
      {selectedCart && (
        <OrderConfirmationModal
          selectedCart={selectedCart}
          orders={orders}
          carts={carts}
          onClose={handleCloseClick}
          onAccept={handleAcceptOrder}
          onReject={handleRejectOrder}
          onCancelOrder={handleCancelOrder}
          onRestoreOrder={handleRestoreOrder}
        />
      )}
      <ConfirmDialog
        open={acceptDialogOpen}
        onClose={() => setAcceptDialogOpen(false)}
        onConfirm={handleConfirmAccept}
        title="注文承諾"
        message="この注文を承諾しますか？"
        confirmText="承諾"
        cancelText="閉じる"
        confirmColor="success"
        closeColor="inherit"
      />
      <ConfirmDialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleConfirmReject}
        title="注文拒否"
        message="この注文を拒否しますか？"
        confirmText="拒否"
        cancelText="閉じる"
        confirmColor="error"
        closeColor="inherit"
      />
      <ConfirmDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleConfirmCancel}
        title="注文キャンセル"
        message="この注文をキャンセルしますか？"
        confirmText="キャンセル"
        cancelText="閉じる"
        confirmColor="error"
        closeColor="inherit"
      />
      <ConfirmDialog
        open={restoreDialogOpen}
        onClose={() => setRestoreDialogOpen(false)}
        onConfirm={handleConfirmRestore}
        title="注文復元"
        message="この注文を受付中に戻しますか？"
        confirmText="復元"
        cancelText="閉じる"
        confirmColor="primary"
        closeColor="inherit"
      />
    </Box>
  );
};

export default OrderManagement;
